/**
 * 数字人逐句对照编辑区的纯状态逻辑(2026-10-10「追爆出口原话复用」§3)。
 *
 * 用户拍板:默认 **A 原样翻拍** —— 原话几乎全留,只换品牌/产品;留下的原话里带参考片的
 * 数字/故事 → 标黄,出片前要用户确认(不自动挖空)。设计原则:**不拦原话,拦「别人家的事实
 * 变成用户的事实」**。
 *
 * 「最终口播」大框仍是唯一上送源(引擎零改动):本模块只负责「逐句 → 拼成一段」与黄标判据,
 * 手改大框后的「已手改」状态在页面里管。
 */
import type {
  BlueprintLine,
  BlueprintLineRole,
  RefFact,
} from "@/entities/explosive/model/blueprintLines";

import type { DhBlueprintRewriteLine, DhScriptLine } from "../api/digitalHumanService";

export type DhLineMode = "keep" | "swap" | "rewrite";
export type DhLinePreset = "verbatim" | "keepHookCta" | "allMine";

export const DH_LINE_MODE_LABEL: Record<DhLineMode, string> = {
  keep: "原话",
  swap: "套句式",
  rewrite: "按我的产品重写",
};

export const DH_LINE_PRESETS: { key: DhLinePreset; label: string; hint: string }[] = [
  { key: "verbatim", label: "原样翻拍", hint: "原话全留,选了产品只换品牌/产品名" },
  { key: "keepHookCta", label: "留钩子收尾,中间换我的", hint: "开头共鸣和收尾用原话" },
  { key: "allMine", label: "全部按我的产品写", hint: "每句按公式 × 你的产品重写" },
];

interface GeneratedLine {
  text: string;
  broll: boolean;
  beat: string;
  unverifiedNumbers: string[];
  /** 1010:改写稿里用户事实撑不住的承诺 / 参考片产品名(ai 守卫重试后仍在)。 */
  unverifiedFacts: string[];
}

export interface DhLineRow {
  idx: number;
  /** 参考片原话(added 句=生成文本本身)。 */
  original: string;
  role: BlueprintLineRole;
  refFacts: RefFact[];
  mode: DhLineMode;
  /** C2 产出按档位缓存:切回原话再切回来不必重烧。 */
  generated: Partial<Record<"swap" | "rewrite", GeneratedLine>>;
  /** 「加一句我的产品介绍」生成的句子。 */
  added: boolean;
  /** 黄标句「我确认可以用」。 */
  confirmed: boolean;
}

export function rowsFromLines(lines: BlueprintLine[]): DhLineRow[] {
  return lines.map((l) => ({
    idx: l.idx,
    original: l.text,
    role: l.role,
    refFacts: l.refFacts,
    mode: "keep",
    generated: {},
    added: false,
    confirmed: false,
  }));
}

/** 当前档位下这一句念什么:原话 / 生成稿;待生成时先显示原话。 */
export function rowText(row: DhLineRow): string {
  if (row.mode === "keep") return row.original;
  return row.generated[row.mode]?.text ?? row.original;
}

/** 选了套句式/重写但还没生成。 */
export function isPending(row: DhLineRow): boolean {
  return row.mode !== "keep" && !row.generated[row.mode];
}

function showsOriginal(row: DhLineRow): boolean {
  return !row.added && (row.mode === "keep" || isPending(row));
}

/**
 * 黄标:显示的是原话且含参考片事实(number/story/brand/product/promise/cert 全算),
 * 或 C2 回了 unverifiedNumbers(改写句里的数字不在产品事实里)。
 */
export function flagOf(row: DhLineRow): {
  facts: RefFact[];
  unverified: string[];
  unverifiedFacts: string[];
} {
  const facts = showsOriginal(row) ? row.refFacts : [];
  const gen = row.mode === "keep" ? undefined : row.generated[row.mode];
  return {
    facts,
    unverified: gen?.unverifiedNumbers ?? [],
    unverifiedFacts: gen?.unverifiedFacts ?? [],
  };
}

/** 选产品就会被自动替换掉的事实(品牌/产品名)。 */
const REPLACEABLE_KINDS = new Set(["brand", "product"]);

/**
 * 展示口径(1010 打磨:进页 7 句 6 黄,用户只会全勾,黄标失去意义):
 * 还没选产品时,只含品牌/产品名的事实不标黄 —— 选了产品这些句会自动切「套句式」换成你的,
 * 改成一行灰色提示;数字/故事/承诺/资质照旧黄标。选了产品后用户仍把这句留成原话 = 有意保留别家
 * 产品名,回到黄标。★出片闸 `unconfirmedFlagged` 不跟着放松:没选产品就出片,这些句照样要确认。
 */
export function displayFlagOf(
  row: DhLineRow,
  hasProduct: boolean
): {
  confirm: RefFact[];
  replaceHint: RefFact[];
  unverified: string[];
  unverifiedFacts: string[];
} {
  const { facts, unverified, unverifiedFacts } = flagOf(row);
  if (hasProduct) return { confirm: facts, replaceHint: [], unverified, unverifiedFacts };
  return {
    confirm: facts.filter((f) => !REPLACEABLE_KINDS.has(f.kind)),
    replaceHint: facts.filter((f) => REPLACEABLE_KINDS.has(f.kind)),
    unverified,
    unverifiedFacts,
  };
}

export function isFlagged(row: DhLineRow): boolean {
  const f = flagOf(row);
  return f.facts.length > 0 || f.unverified.length > 0 || f.unverifiedFacts.length > 0;
}

/** 出片前要弹框的句子:黄标、没勾确认、且这句还在最终口播里(用户手改删掉了就不拦)。 */
export function unconfirmedFlagged(rows: DhLineRow[], script: string): DhLineRow[] {
  return rows.filter((r) => isFlagged(r) && !r.confirmed && script.includes(rowText(r)));
}

export function composeScript(rows: DhLineRow[]): string {
  return rows
    .map(rowText)
    .map((t) => t.trim())
    .filter(Boolean)
    .join("\n");
}

function presetMode(preset: DhLinePreset, role: BlueprintLineRole): DhLineMode {
  if (preset === "verbatim") return "keep";
  if (preset === "allMine") return "rewrite";
  return role === "hook" || role === "resonance" || role === "cta" ? "keep" : "rewrite";
}

function withMode(row: DhLineRow, mode: DhLineMode): DhLineRow {
  if (row.mode === mode) return row;
  return { ...row, mode, confirmed: false };
}

/** A 档自动动作:选了产品后,含参考片品牌/产品名的原话句切「套句式」(留着别家品牌名没意义)。 */
export function autoSwapForProduct(rows: DhLineRow[]): DhLineRow[] {
  return rows.map((r) =>
    !r.added &&
    r.mode === "keep" &&
    r.refFacts.some((f) => f.kind === "brand" || f.kind === "product")
      ? withMode(r, "swap")
      : r
  );
}

/** 三预设 = 批量设每句档位(added 句不动)。A 档且已选产品 → 顺带 autoSwap。 */
export function applyPreset(
  rows: DhLineRow[],
  preset: DhLinePreset,
  hasProduct: boolean
): DhLineRow[] {
  const next = rows.map((r) => (r.added ? r : withMode(r, presetMode(preset, r.role))));
  return preset === "verbatim" && hasProduct ? autoSwapForProduct(next) : next;
}

export function setRowMode(rows: DhLineRow[], idx: number, mode: DhLineMode): DhLineRow[] {
  return rows.map((r) => (r.idx === idx && !r.added ? withMode(r, mode) : r));
}

/** 参考片口播里有没有讲产品的句子(没有 → 给「加一句我的产品介绍」)。 */
export function hasProductLine(rows: DhLineRow[]): boolean {
  return rows.some((r) => r.added || r.role === "selling" || r.role === "price");
}

/** C2 请求体 lines:原句 + 档位(added 句不送 —— 由 addProductLine 重新要,回包整体替换)。 */
export function rewriteRequestLines(
  rows: DhLineRow[]
): { idx: number; text: string; mode: DhLineMode; role: BlueprintLineRole }[] {
  // role 透传给 ai:「加一句产品介绍」按 role=cta 定插入位置(不传 ai 只能按话术正则猜)
  return rows
    .filter((r) => !r.added)
    .map((r) => ({ idx: r.idx, text: r.original, mode: r.mode, role: r.role }));
}

/** 合并 C2 回包:按回包顺序重排(added 句在 cta 之前,由服务端定位);生成文本变了 → 确认作废。 */
export function mergeRewrite(rows: DhLineRow[], resp: DhBlueprintRewriteLine[]): DhLineRow[] {
  const byIdx = new Map(rows.filter((r) => !r.added).map((r) => [r.idx, r]));
  const out: DhLineRow[] = [];
  const seen = new Set<number>();
  for (const l of resp) {
    const gen: GeneratedLine = {
      text: l.text,
      broll: !!l.broll,
      beat: l.beat ?? "",
      unverifiedNumbers: Array.isArray(l.unverifiedNumbers) ? l.unverifiedNumbers : [],
      unverifiedFacts: Array.isArray(l.unverifiedFacts) ? l.unverifiedFacts : [],
    };
    if (l.added) {
      out.push({
        idx: l.idx,
        original: l.text,
        role: "selling",
        refFacts: [],
        mode: "rewrite",
        generated: { rewrite: gen },
        added: true,
        confirmed: false,
      });
      continue;
    }
    const row = byIdx.get(l.idx);
    if (!row) continue;
    seen.add(l.idx);
    if (l.mode === "keep" || row.mode === "keep") {
      out.push(row);
      continue;
    }
    const prev = row.generated[row.mode]?.text;
    out.push({
      ...row,
      generated: { ...row.generated, [row.mode]: gen },
      confirmed: prev === gen.text ? row.confirmed : false,
    });
  }
  // 回包漏了的句子(不该发生)补在末尾,不丢用户的句
  for (const r of rows) if (!r.added && !seen.has(r.idx)) out.push(r);
  return out;
}

/** b-roll 逐句条数据源(keep 句不建议切画面;改写句用服务端建议)。 */
export function linesForBroll(rows: DhLineRow[]): DhScriptLine[] {
  return rows
    .map((r) => {
      const gen = r.mode === "keep" ? undefined : r.generated[r.mode];
      return { text: rowText(r).trim(), broll: gen?.broll ?? false, beat: gen?.beat || null };
    })
    .filter((l) => l.text);
}
